import { startCase } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import {
  TASK_SCHEDULE_LABELS,
  TASK_SCHEDULE_TABLE_HEADERS,
} from 'utils/constants/task';
import {
  SCHEDULE_PATTERNS,
  SCHEDULE_CONSTANTS,
} from 'utils/constants/schedule';
import { days, dayOfMonthOptions } from 'mocks/utils';
import API from '../../../../services/api';
import { DEFAULT_EMPTY_VALUE } from 'utils/constants';
import { siteRequiresPassKey } from 'utils/constants/visitor';
import { transformBasicPersonnel } from 'utils/DataTransform/Personnel/PersonnelList';
import { scheduleValidation } from 'utils/formFields/task';
import { displayTime, displayDate } from 'utils/time';
import {
  validateConditionalRequired,
  validateRequired,
  validationObject,
} from 'utils/validations';
import {
  businessClientValidation,
  contactValidation,
  siteBaseValidation,
} from 'utils/validationSchema';
const {
  Type,
  Assignee,
  StartAfter,
  FinishBefore,
  Days,
  StartTime,
  EndTime,
  StartDate,
  RepeatUntil,
  RepeatsEvery,
  Interval,
  DateOfMonth,
  RescheduleInvalidDates,
  RescheduleWeekends,
  DayOfMonth,
  TimeZone,
} = TASK_SCHEDULE_TABLE_HEADERS;

export const getScheduleFields = ({ taskType, activeTab }) => {
  return [
    {
      field: 'assignToUserIDs',
      label: 'Assignee',
      size: 'half',
      type: 'async-select',
      fetchOptions: API.fetchUserList,
      dataTransform: ({ data }) => transformBasicPersonnel(data),
      fetchFilters: {
        service: [taskType.category],
      },
      optionLabelKey: 'name',
      multiple: true,
      fixedOptions: [{ id: 'ALL', name: SCHEDULE_CONSTANTS.allProviders }],
      onChangeCallback: (selectedValues, values, onChange) => {
        const currentSelectedValue = selectedValues.filter(
          item => !values.some(v => v.id === item.id),
        );

        if (currentSelectedValue.some(item => item.id === 'ALL')) {
          onChange([{ id: 'ALL', name: SCHEDULE_CONSTANTS.allProviders }]);
          return;
        }

        if (selectedValues.some(item => item.id === 'ALL')) {
          onChange(selectedValues.filter(item => item.id !== 'ALL'));
        }
      },
      limitTags: 4,
    },
    {
      field: 'scheduleType',
      label: 'Schedule Type',
      type: 'select',
      required: true,
      options: [
        {
          id: SCHEDULE_PATTERNS.onceOff.id,
          name: SCHEDULE_PATTERNS.onceOff.label,
          displayName: TASK_SCHEDULE_LABELS.onceOff,
        },
        {
          id: SCHEDULE_PATTERNS.simpleInterval.id,
          name: SCHEDULE_PATTERNS.simpleInterval.label,
          displayName: TASK_SCHEDULE_LABELS.simpleInterval,
        },
        {
          id: SCHEDULE_PATTERNS.weekly.id,
          name: SCHEDULE_PATTERNS.weekly.label,
          displayName: TASK_SCHEDULE_LABELS.weekly,
        },
        {
          id: SCHEDULE_PATTERNS.dateOfMonth.id,
          name: SCHEDULE_PATTERNS.dateOfMonth.label,
          displayName: TASK_SCHEDULE_LABELS.dateOfMonth,
        },
        {
          id: SCHEDULE_PATTERNS.dayOfMonth.id,
          name: SCHEDULE_PATTERNS.dayOfMonth.label,
          displayName: TASK_SCHEDULE_LABELS.dayOfMonth,
        },
      ],
      size: 'half',
      handleSelectChange: ({ e, handleChange, props, value, name }) => {
        // sets default value of schedule type as onceOff if clear button is pressed
        if (typeof handleChange === 'function') {
          e.target.value = value.id;
          e.target.name = name;
          handleChange(e);
        }
        // this sets the active tab to the selected option
        props.setFieldValue('schedule.activeTab', value.name);
      },
    },
    {
      field: 'schedule',
      type: 'schedule',
      size: 'full',
      hideAddSchedule: true,
      activeTab,
    },
  ];
};

export const initialValues = props => {
  return {
    assignToUserIDs: [],
    site: '',
    newBusinessClient: false,
    startAfter: null,
    finishBefore: null,
    scheduleType: SCHEDULE_PATTERNS.onceOff.id,
    schedule: {
      activeTab: SCHEDULE_PATTERNS.onceOff.label,
    },
    ...props,
  };
};

export const validationSchema = ({
  newSite,
  newBusinessClient,
  newContact,
  newSiteContact,
  activeSchedule,
}) => {
  const validation = {
    ...(newSite && {
      ...omit(siteBaseValidation, ['locations', siteRequiresPassKey]),
      businessClient: validateConditionalRequired({
        conditionKey: 'newBusinessClient',
        conditionValue: false,
      }),
      siteLocations: validateRequired(),
    }),
    ...((newContact || newSiteContact) && { ...contactValidation }),
    ...(newBusinessClient && {
      ...businessClientValidation,
    }),
    schedule: scheduleValidation({ pattern: activeSchedule }),
  };

  return validationObject(validation);
};

export const MULTI_SCHEDULE_COLUMNS = [
  {
    headerName: Type,
    field: 'type',
  },
  // {
  //   headerName: 'Site',
  //   field: 'site',
  // },
  // {
  //   headerName: 'Business Client',
  //   field: 'businessClient',
  // },
  {
    headerName: Assignee,
    field: 'assignee',
  },
  {
    headerName: StartAfter,
    field: 'startAfter',
  },
  {
    headerName: FinishBefore,
    field: 'finishBefore',
  },
  {
    headerName: Days,
    field: 'days',
  },
  {
    headerName: StartTime,
    field: 'startTime',
  },
  {
    headerName: EndTime,
    field: 'endTime',
  },
  {
    headerName: StartDate,
    field: 'startDate',
  },
  {
    headerName: RepeatUntil,
    field: 'repeatUntil',
  },
  {
    headerName: RepeatsEvery,
    field: 'repeatsEvery',
  },
  {
    headerName: Interval,
    field: 'interval',
  },
  {
    headerName: DateOfMonth,
    field: 'dateOfMonth',
  },
  {
    headerName: RescheduleInvalidDates,
    field: 'rescheduleInvalidDates',
  },
  {
    headerName: RescheduleWeekends,
    field: 'rescheduleWeekends',
  },
  {
    headerName: DayOfMonth,
    field: 'dayOfMonth',
  },
  {
    headerName: TimeZone,
    field: 'timezone',
  },
];

export const getBusinessClientName = item => {
  if (item.newSite) {
    if (item.newBusinessClient) return item.companyName;
    return item.businessClient ? item.businessClient.name : item.businessClient;
  }

  return item.site ? item.site.businessClientName : DEFAULT_EMPTY_VALUE;
};

export const transformTableMultiSchedule = data => {
  return data.map(item => {
    const { schedule } = item;
    const type = schedule ? schedule.activeTab : '';
    const scheduleValue = type ? schedule[type] : item;
    const dayOfMonthString = [...dayOfMonthOptions][
      scheduleValue.dayOfMonth - 1
    ]?.name;
    // when the date is selected from the Calendar it is a date object which cannot be rendered as a react child
    // so it needs to be converted to display string
    const scheduleStartAfter =
      typeof schedule.startAfter === 'string'
        ? scheduleValue.startAfter
        : displayTime({ timestamp: scheduleValue.startAfter });
    const scheduleFinishBefore =
      typeof schedule.finishBefore === 'string'
        ? scheduleValue.finishBefore
        : displayTime({ timestamp: scheduleValue.finishBefore });

    return {
      rowData: {
        type: type ? startCase(type) : DEFAULT_EMPTY_VALUE,
        assignee: isEmpty(item.assignToUserIDs)
          ? SCHEDULE_CONSTANTS.allProviders
          : item.assignToUserIDs.map(item => item.name).toString(),
        startAfter: scheduleStartAfter,
        finishBefore: scheduleValue.finishBefore
          ? scheduleFinishBefore
          : DEFAULT_EMPTY_VALUE,
        days: isEmpty(scheduleValue.days)
          ? DEFAULT_EMPTY_VALUE
          : scheduleValue.days
              .map(day => days.find(d => d.id === day).name)
              .join(', '),
        startTime: scheduleValue.startTimeOnly || DEFAULT_EMPTY_VALUE,
        endTime: scheduleValue.endTimeOnly || DEFAULT_EMPTY_VALUE,
        startDate: scheduleValue.startDate
          ? displayDate({
              date: scheduleValue.startDate,
              format: 'DD-MM-YYYY',
            })
          : DEFAULT_EMPTY_VALUE,
        repeatUntil: scheduleValue.repeatUntil
          ? displayDate({
              date: scheduleValue.repeatUntil,
              format: 'DD-MM-YYYY',
            })
          : DEFAULT_EMPTY_VALUE,
        repeatsEvery: scheduleValue.repeatsEvery || DEFAULT_EMPTY_VALUE,
        interval: scheduleValue.interval || DEFAULT_EMPTY_VALUE,
        dateOfMonth: scheduleValue.dateOfMonth || DEFAULT_EMPTY_VALUE,
        rescheduleInvalidDates:
          scheduleValue.rescheduleInvalidDates || DEFAULT_EMPTY_VALUE,
        rescheduleWeekends:
          scheduleValue.rescheduleWeekends || DEFAULT_EMPTY_VALUE,
        dayOfMonth: dayOfMonthString || DEFAULT_EMPTY_VALUE,
        timezone: scheduleValue.timezone,
      },
      allowEdit: true,
      allowDelete: true,
      rawData: item,
    };
  });
};
